"use client";

import { useEffect, useState } from "react";

import { RomaReserveSheetButton } from "@/components/roma/dark-funnel/roma-reserve-sheet-button";
import { useRomaReserveSheet } from "@/components/roma/roma-reserve-sheet-context";
import { useMarketingLocationContext } from "@/lib/marketing-location-context";

function mapsDirectionsHref(destination: string) {
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
}

/**
 * Roma-only fixed bottom quick-action bar for the /menu surface (mobile).
 * Meta-traffic goal pairing for a dine-in restaurant: Reserve is primary
 * (native location sheet when available), Get Directions is secondary via a
 * two-location picker. Ordering stays available inside the item dialog.
 * Hidden while the footer is on screen so it never covers the footer's CTAs.
 * Height comes from `--roma-menu-bar-height` (tuscan-globals.css); the menu
 * shell reserves the same height plus the safe-area inset as bottom padding.
 * The background is opaque so content scrolling underneath never shows
 * through, and the bar must render outside `space-y-*` stacks (a stack
 * margin-bottom lifts a `bottom-0` fixed bar off the viewport edge).
 */
export function RomaMenuMobileCtaBar() {
  const { siteConfig } = useMarketingLocationContext();
  const reserveSheet = useRomaReserveSheet();
  const [footerInView, setFooterInView] = useState(false);
  const [directionsOpen, setDirectionsOpen] = useState(false);

  useEffect(() => {
    const footer = document.querySelector("footer");
    if (!footer) return;

    const observer = new IntersectionObserver(
      (entries) => {
        setFooterInView(entries.some((entry) => entry.isIntersecting));
      },
      { threshold: 0 },
    );

    observer.observe(footer);
    return () => observer.disconnect();
  }, []);

  const reservationUrl = siteConfig.reservationUrl;
  const pickerLocations = reserveSheet?.locations ?? [];
  const fallbackAddress = [
    siteConfig.address.line1,
    siteConfig.address.city,
    siteConfig.address.state,
    siteConfig.address.zip,
  ]
    .filter(Boolean)
    .join(", ");
  const canReserve = Boolean(reserveSheet?.enabled || reservationUrl);
  const canGetDirections = Boolean(
    pickerLocations.length || fallbackAddress,
  );
  const visible = !footerInView;

  if (!canReserve && !canGetDirections) return null;

  return (
    <nav
      aria-label="Menu quick actions"
      aria-hidden={!visible}
      // `inert` keeps the buttons out of the tab order while hidden;
      // pointer-events-none alone would leave them keyboard-focusable.
      inert={!visible}
      className={`fixed inset-x-0 bottom-0 z-40 flex min-h-[calc(var(--roma-menu-bar-height)+env(safe-area-inset-bottom,0px))] flex-col justify-end border-t border-white/10 bg-[#0d0d0d] px-4 pb-[calc(env(safe-area-inset-bottom,0px)+0.75rem)] pt-3 shadow-[0_-16px_48px_-32px_rgba(0,0,0,0.85)] transition-[transform,opacity] duration-300 ease-out md:hidden motion-reduce:transition-none ${
        visible
          ? "translate-y-0 opacity-100"
          : "pointer-events-none translate-y-full opacity-0"
      }`}
    >
      <div
        className={`grid gap-2 ${
          canReserve && canGetDirections ? "grid-cols-2" : "grid-cols-1"
        }`}
      >
        {canReserve ? (
          <RomaReserveSheetButton
            fallbackHref={reservationUrl}
            trackLocation="menu_mobile_sticky"
            className="roma-funnel-btn-primary flex min-h-12 items-center justify-center rounded-lg border border-[#b91c1c] bg-[#b91c1c] px-4 text-[15px] font-semibold text-white transition-colors hover:border-[#cf1f1f] hover:bg-[#cf1f1f] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--roma-star)]"
          >
            Reserve
          </RomaReserveSheetButton>
        ) : null}
        {canGetDirections ? (
          <button
            type="button"
            aria-expanded={directionsOpen}
            aria-controls="menu-directions-picker"
            onClick={() => setDirectionsOpen((open) => !open)}
            className="flex min-h-12 items-center justify-center rounded-lg border border-white/25 px-4 text-[15px] font-semibold text-white transition-colors hover:border-white/45 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--roma-star)]"
          >
            Get directions
          </button>
        ) : null}
      </div>
      {canGetDirections ? (
        <div
          id="menu-directions-picker"
          hidden={!directionsOpen}
          className="order-first mb-3 overflow-hidden rounded-lg border border-white/15"
        >
          {pickerLocations.length ? (
            pickerLocations.map((location) => (
              <a
                key={location.slug}
                href={mapsDirectionsHref(
                  `${location.addressLine1}, ${location.addressLine2}`,
                )}
                target="_blank"
                rel="noopener noreferrer"
                data-track="directions_clicked"
                data-track-location="menu_mobile_sticky"
                data-track-label={location.slug}
                onClick={() => setDirectionsOpen(false)}
                className="flex items-baseline justify-between gap-3 border-b border-white/10 bg-[#161616] px-4 py-3 last:border-b-0 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-[var(--roma-star)]"
              >
                <span className="text-[15px] font-semibold text-white">
                  {location.shortName}
                </span>
                <span className="truncate text-[13px] text-[#d4d4d4]">
                  {location.addressLine1}
                </span>
              </a>
            ))
          ) : (
            <a
              href={mapsDirectionsHref(fallbackAddress)}
              target="_blank"
              rel="noopener noreferrer"
              data-track="directions_clicked"
              data-track-location="menu_mobile_sticky"
              onClick={() => setDirectionsOpen(false)}
              className="flex items-baseline justify-between gap-3 bg-[#161616] px-4 py-3 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-[var(--roma-star)]"
            >
              <span className="text-[15px] font-semibold text-white">
                {siteConfig.name}
              </span>
              <span className="truncate text-[13px] text-[#d4d4d4]">
                {siteConfig.address.line1}
              </span>
            </a>
          )}
        </div>
      ) : null}
    </nav>
  );
}

export default RomaMenuMobileCtaBar;
